/* eslint-disable no-console, no-undef */
import React, { useCallback, useRef, useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Card, Tag, Spin, Switch } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { usePostMonitoringCommandMutation } from "../../../redux/monitoring/monitoringAPI";
import { updateSendCommand } from "../../../redux/monitoring/monitoringSlice";
// FLAT BUFFERS
import { decodeFrontEndMessage } from "../../../flatbuffers/decoders/frontEndMessageDecoder";
import {
  applyDeltaToState,
  createDefaultState,
  DisplayMapMode,
} from "../../../flatbuffers/decoders/applyDeltaToState";
import { createUiCmdPayload } from "../../../flatbuffers/builders/uiCmdBuilder";
import { FaultMessageT } from "../../../flatbuffers/generated/fault/fault-message";
// MONITORING STATE
import { useBankDispatch } from "../socketMessageState/BankMessagesContext";
// SOCKET EMITTER
import socketEmitter from "./MonitoringWebSocketEmitter";
// CHANGE DETECTION
import hasStateChanged from "./hasStateChanged";
// AUTHORIZATION
import GETJwtTokenSocket from "../../../redux/authentication/authenticationSocket";
import GETJwtToken from "../../../redux/authentication/authentication";
// CUSTOM HOOKS
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// INTERFACES
import { store, RootState } from "../../../app/store";
import {
  LayoutInterface,
  SocketMessageInterface,
} from "../../../interfaces/Monitoring.interface";
import { MessageInterface } from "../../../interfaces/Message.interface";
import { FaultContent } from "../../../flatbuffers/generated/fault/fault-content";
import { uuidToString } from "../../../utils/uuid";

const FAULT_PERSISTENCE_KEY_PREFIX = "liftnet_expired_faults_";

// Helper to create composite fault key from typed fault
const createFaultKey = (
  bankId: string,
  liftId: string,
  faultContent: FaultContent,
  code: number
): string => `${bankId}:${liftId}:${faultContent}:${code}`;

// Helper to create fault key prefix for a bank/lift combination
const createFaultKeyPrefix = (bankId: string, liftId: string): string =>
  `${bankId}:${liftId}:`;

// Map FaultContent to Messages API device_type for debounce lookup
const faultContentToDeviceType = (fc: FaultContent): string => {
  switch (fc) {
    case FaultContent.ElevatorFaultMessage:
      return "ELEVATOR";
    case FaultContent.EscalatorFaultMessage:
      return "ESCALATOR";
    default:
      return "";
  }
};

// LocalStorage helpers for fault persistence (stores stringified fault keys)
const getPersistedFaults = (bankId: string): Set<string> => {
  const key = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankId}`;
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to load persisted faults:", e);
  }
  return new Set();
};

const savePersistedFaults = (bankId: string, faults: Set<string>) => {
  const key = `${FAULT_PERSISTENCE_KEY_PREFIX}${bankId}`;
  try {
    if (faults.size > 0) {
      localStorage.setItem(key, JSON.stringify(Array.from(faults)));
    } else {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.error("❌ [FAULT_TIMER] Failed to save persisted faults:", e);
  }
};

// Serialize a FaultMessageT to a persistence-safe string
const faultMessageToString = (f: FaultMessageT): string =>
  `${f.faultType}:${f.fault?.message}`;

function MonitoringWebSocketConnection({
  serverId,
  serverName,
  channelType,
  liftName,
  liftId,
  channelId,
  layouts,
}: {
  serverId: string;
  serverName: string;
  channelType: string;
  liftName: string;
  liftId: string;
  channelId: string;
  layouts: LayoutInterface[];
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const socket = useRef<WebSocket | null>(null);
  const SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_EDGE_SERVER;
  const myIntervalCheckSocketConnection: any = useRef(null);
  const CONNECTING = 0;
  const OPEN = 1;
  const CLOSING = 2;
  const CLOSED = 3;
  const dispatch = useBankDispatch();
  const dispatchRedux = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [enabledAutoRefresh, setEnabledAutoRefresh] = useState<boolean>(true);
  const [enabledSendPing, setEnabledSendPing] = useState<boolean>(true);

  // ************************************************ */
  // FAULT TIMER STATE ****************************** */
  const activeFaults = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const expiredFaults = useRef<Set<string>>(new Set());
  const persistedFaultsByBank = useRef<Map<string, Set<string>>>(new Map());
  const messageDebounceMap = useRef<Map<string, number>>(new Map());
  const lastMessagesByLift = useRef<Map<string, any>>(new Map());
  const liftStateRef = useRef<Map<string, SocketMessageInterface>>(new Map());
  const pendingDispatches = useRef<Map<string, any>>(new Map());
  const rafHandle = useRef<number | null>(null);
  const displayMapModes = useRef<Map<string, DisplayMapMode>>(new Map());

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const showQueueInfo = useSelector(
    (state: RootState) => state.monitoring.showQueueInfo
  );
  const stopSocketConnection = useSelector(
    (state: RootState) => state.monitoring.stopSocketConnection
  );
  const sendCommand = useSelector(
    (state: RootState) => state.monitoring.sendCommand
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [postCommand, { isLoading: isLoadingPostCommand }] =
    usePostMonitoringCommandMutation();
  const { messages } = useGetAllMessages({ deviceType: "", messageTypeId: "" });

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const flushPendingDispatches = useCallback(() => {
    if (store.getState().monitoring.openReplayPanel) {
      pendingDispatches.current.clear();
      rafHandle.current = null;
      return;
    }
    pendingDispatches.current.forEach((payload) => {
      dispatch({
        type: "UPDATE_MESSAGE",
        payload,
      });
    });
    pendingDispatches.current.clear();
    rafHandle.current = null;
  }, [dispatch]);

  const scheduleDispatch = useCallback(
    (stateKey: string, payloadData: any) => {
      pendingDispatches.current.set(stateKey, payloadData);
      if (rafHandle.current === null) {
        rafHandle.current = requestAnimationFrame(flushPendingDispatches);
      }
    },
    [flushPendingDispatches]
  );

  const getDebounceTime = useCallback(
    (faultContent: FaultContent, code: number): number => {
      const deviceType = faultContentToDeviceType(faultContent);
      return messageDebounceMap.current.get(`${deviceType}:${code}`) ?? 0;
    },
    []
  );

  const shouldFaultBlink = useCallback(
    (
      activeFaultList: FaultMessageT[],
      bankIdStr: string,
      targetLiftId: string
    ): boolean => {
      const persistedFaults =
        persistedFaultsByBank.current.get(bankIdStr) || new Set();
      const faultKeyPrefix = createFaultKeyPrefix(bankIdStr, targetLiftId);
      const allExpiredFaults = Array.from(expiredFaults.current);
      const hasExpiredFaultsForThisLift = allExpiredFaults.some((key) =>
        key.startsWith(faultKeyPrefix)
      );
      const matchesPersistedFault = activeFaultList.some((f) =>
        persistedFaults.has(faultMessageToString(f))
      );

      return hasExpiredFaultsForThisLift || matchesPersistedFault;
    },
    []
  );

  const manageFaultTimers = useCallback(
    (
      faultsWithDebounce: Array<{
        fault: FaultMessageT;
        faultStr: string;
        debounceMs: number;
      }>,
      bankIdStr: string,
      targetLiftId: string
    ) => {
      const incomingFaultKeys = new Set(
        faultsWithDebounce.map((f) =>
          createFaultKey(
            bankIdStr,
            targetLiftId,
            f.fault.faultType,
            f.fault.fault?.message ?? 0
          )
        )
      );
      const currentFaults = activeFaults.current;
      const faultKeyPrefix = createFaultKeyPrefix(bankIdStr, targetLiftId);

      const triggerImmediateUpdate = () => {
        const key = createFaultKeyPrefix(bankIdStr, targetLiftId);
        const lastMsg = lastMessagesByLift.current.get(key);
        if (lastMsg) {
          const shouldBlink = shouldFaultBlink(
            lastMsg.activeFaults,
            bankIdStr,
            targetLiftId
          );
          if (!store.getState().monitoring.openReplayPanel) {
            dispatch({
              type: "UPDATE_MESSAGE",
              payload: { ...lastMsg, faults: shouldBlink },
            });
          }
        }
      };

      const updatePersistence = (faultStr: string, shouldAdd: boolean) => {
        try {
          const currentSet =
            persistedFaultsByBank.current.get(bankIdStr) || new Set();
          if (shouldAdd) currentSet.add(faultStr);
          else currentSet.delete(faultStr);
          persistedFaultsByBank.current.set(bankIdStr, currentSet);
          savePersistedFaults(bankIdStr, currentSet);
        } catch (e) {
          console.error("Error updating fault persistence:", e);
        }
      };

      // 1. START TIMERS
      faultsWithDebounce.forEach(({ fault, faultStr, debounceMs }) => {
        const faultKey = createFaultKey(
          bankIdStr,
          targetLiftId,
          fault.faultType,
          fault.fault?.message ?? 0
        );
        if (
          !currentFaults.has(faultKey) &&
          !expiredFaults.current.has(faultKey)
        ) {
          if (debounceMs === 0) {
            expiredFaults.current.add(faultKey);
            updatePersistence(faultStr, true);
            triggerImmediateUpdate();
          } else {
            const timer = setTimeout(() => {
              expiredFaults.current.add(faultKey);
              currentFaults.delete(faultKey);
              updatePersistence(faultStr, true);
              triggerImmediateUpdate();
            }, debounceMs);
            currentFaults.set(faultKey, timer);
          }
        }
      });

      // 2. CANCEL TIMERS
      Array.from(currentFaults.keys()).forEach((faultKey) => {
        if (faultKey.startsWith(faultKeyPrefix)) {
          if (!incomingFaultKeys.has(faultKey)) {
            const timer = currentFaults.get(faultKey);
            if (timer) clearTimeout(timer);
            currentFaults.delete(faultKey);
            // Extract faultContent:code from key for persistence
            const suffix = faultKey.substring(faultKeyPrefix.length);
            updatePersistence(suffix, false);
          }
        }
      });

      // 3. STOP BLINKING
      Array.from(expiredFaults.current).forEach((faultKey) => {
        if (faultKey.startsWith(faultKeyPrefix)) {
          if (!incomingFaultKeys.has(faultKey)) {
            expiredFaults.current.delete(faultKey);
            const suffix = faultKey.substring(faultKeyPrefix.length);
            updatePersistence(suffix, false);
            triggerImmediateUpdate();
          }
        }
      });
    },
    [dispatch, shouldFaultBlink]
  );

  const onMessageFlatBuffer = useCallback(
    async (socketMessage: ArrayBuffer | Blob | string) => {
      // Skip all processing when replay panel is open
      if (store.getState().monitoring.openReplayPanel) {
        return;
      }
      if (typeof socketMessage === "string") {
        console.error("❌ WS sent STRING instead of binary:", socketMessage);
        return;
      }
      let buffer: ArrayBuffer;
      if (socketMessage instanceof Blob) {
        buffer = await socketMessage.arrayBuffer();
      } else {
        buffer = socketMessage;
      }

      const bytes = new Uint8Array(buffer);
      try {
        const delta = decodeFrontEndMessage(bytes);
        if (!delta) return;

        const bankIdStr = uuidToString(delta.bankId);
        const liftIdStr = uuidToString(delta.liftId);
        const stateKey = `${bankIdStr}:${liftIdStr}`;

        // Look up or create default state for this lift
        const existingState =
          liftStateRef.current.get(stateKey) ||
          createDefaultState(delta.bankId, delta.liftId);

        // Apply the delta to get merged state
        const mergedState = applyDeltaToState(
          existingState,
          delta,
          displayMapModes.current.get(bankIdStr)
        );

        // Run fault timer logic with the merged activeFaults
        const { activeFaults: activeFaultList } = mergedState;
        const faultsWithDebounce = activeFaultList.map((fault) => ({
          fault,
          faultStr: faultMessageToString(fault),
          debounceMs: getDebounceTime(
            fault.faultType,
            fault.fault?.message ?? 0
          ),
        }));
        manageFaultTimers(faultsWithDebounce, bankIdStr, liftIdStr);

        const shouldBlink = shouldFaultBlink(
          activeFaultList,
          bankIdStr,
          liftIdStr
        );

        const isSocketClosed = socket.current?.readyState === WebSocket.CLOSED;

        const payloadData: SocketMessageInterface = {
          ...mergedState,
          faults: shouldBlink,
          connected: mergedState.connected && !isSocketClosed,
        };

        // Save merged state for future delta accumulation
        liftStateRef.current.set(stateKey, mergedState);

        // Save to ref for timer-triggered updates
        const liftKey = createFaultKeyPrefix(bankIdStr, liftIdStr);
        const prevPayload = lastMessagesByLift.current.get(liftKey);
        lastMessagesByLift.current.set(liftKey, payloadData);

        // Only schedule dispatch if something meaningful changed
        if (hasStateChanged(prevPayload, payloadData)) {
          scheduleDispatch(stateKey, payloadData);
        }
      } catch (err) {
        console.error("❌ Decoder FAILED", err);
      }
    },
    [manageFaultTimers, shouldFaultBlink, getDebounceTime, scheduleDispatch]
  );

  const refreshAll = useCallback(async () => {
    const token = await GETJwtToken();
    const BODY = {
      token,
      body: {
        lift_name: liftName,
        lift_id: liftId,
        channel_id: "",
        key_id: "",
        target_value: 0,
      },
      serverId,
    };
    postCommand(BODY);
  }, [liftName, liftId, serverId, postCommand]);

  // Keep stable refs for callbacks that change identity frequently.
  // The socket event handlers read through these refs so the socket
  // lifecycle useEffect never re-runs due to callback identity changes.
  const onMessageRef = useRef(onMessageFlatBuffer);
  onMessageRef.current = onMessageFlatBuffer;

  const onSocketOpenRef = useRef(() => {
    if (channelType !== "ADAPTER") refreshAll();
  });
  useEffect(() => {
    onSocketOpenRef.current = () => {
      if (channelType !== "ADAPTER") refreshAll();
    };
  }, [channelType, refreshAll]);

  const onConnect = useCallback(async () => {
    if (socket.current?.readyState !== WebSocket.OPEN) {
      const token = await GETJwtTokenSocket();
      socket.current = new WebSocket(
        `${SOCKET_URL}?serverID=${serverId}&access_token=${token}`
      );
      socket.current.addEventListener("open", () => {
        onSocketOpenRef.current();
      });
      socket.current.addEventListener("close", () => {});
      socket.current.addEventListener("message", (event: any) => {
        onMessageRef.current(event.data);
      });
    }
  }, [serverId, SOCKET_URL]);

  const onDisconnect = useCallback(() => {
    socket.current?.close();
  }, []);

  // Use a ref for enabledAutoRefresh so the interval callback is always current
  const enabledAutoRefreshRef = useRef(enabledAutoRefresh);
  enabledAutoRefreshRef.current = enabledAutoRefresh;

  const onConnectRef = useRef(onConnect);
  onConnectRef.current = onConnect;

  useEffect(() => {
    if (messages && messages.length > 0) {
      const map = new Map<string, number>();
      messages.forEach((msg: MessageInterface) => {
        if (msg.enum_id !== null && msg.enum_id !== undefined) {
          map.set(`${msg.device_type}:${msg.enum_id}`, msg.debounce_time ?? 0);
        }
      });
      messageDebounceMap.current = map;
    }
  }, [messages]);

  // Load persisted faults and display map modes whenever layouts change
  useEffect(() => {
    layouts.forEach((layout) => {
      const persisted = getPersistedFaults(layout.idBank);
      if (persisted.size > 0) {
        persistedFaultsByBank.current.set(layout.idBank, persisted);
      }
      // Cache per-bank display map mode from adapter config
      const mode =
        (layout as any).adapter?.socket_message_priority?.displayMapMode ??
        "accumulate";
      displayMapModes.current.set(layout.idBank, mode);
    });
  }, [layouts]);

  // Socket connection lifecycle -- only depends on serverId.
  // All callbacks are accessed through stable refs so that identity changes
  // in useCallback chains never cause socket teardown + reconnect, which
  // would discard the LiftCloud hashmap replay (serviceMode, etc.).
  useEffect(() => {
    if (serverId) {
      onConnectRef.current();
      myIntervalCheckSocketConnection.current = setInterval(() => {
        if (
          socket.current?.readyState === WebSocket.CLOSED &&
          enabledAutoRefreshRef.current
        ) {
          onConnectRef.current();
        }
      }, 30 * 1000);
    }
    return () => {
      socketEmitter.removeAllListeners("sendCommand");
      socket.current?.close();
      if (myIntervalCheckSocketConnection.current)
        clearInterval(myIntervalCheckSocketConnection.current);
      activeFaults.current.forEach((timer) => clearTimeout(timer));
      activeFaults.current.clear();
      expiredFaults.current.clear();
      liftStateRef.current.clear();
      if (rafHandle.current !== null) {
        cancelAnimationFrame(rafHandle.current);
        rafHandle.current = null;
      }
      pendingDispatches.current.clear();
    };
  }, [serverId]);

  useEffect(() => {
    if (stopSocketConnection) socket.current?.close();
    else onConnectRef.current();
  }, [stopSocketConnection]);

  // Send command effect (from development)
  useEffect(() => {
    if (
      sendCommand.sendCommand &&
      socket.current?.readyState === WebSocket.OPEN
    ) {
      const payload = createUiCmdPayload({
        message: sendCommand.body.command,
        position: sendCommand.body.position,
        active: sendCommand.body.active,
        channelId,
        bankId: sendCommand.body.bankId,
        liftId: sendCommand.body.liftId,
      });
      socket.current.send(payload);
      dispatchRedux(
        updateSendCommand({ sendCommand: false, serverId: "", body: {} })
      );
    }
  }, [sendCommand, channelId, dispatchRedux]);

  return (
    <Spin spinning={isLoadingPostCommand}>
      {showQueueInfo && (
        <div>
          <Card
            title={serverName}
            style={{ width: 550, marginLeft: "15px", marginTop: "10px" }}
            extra={
              <div>
                {socket.current?.readyState !== OPEN && (
                  <Button onClick={onConnect}>Connect</Button>
                )}
                {socket.current?.readyState === OPEN && (
                  <Button onClick={onDisconnect}>Disconnect</Button>
                )}
                <Switch
                  onChange={setEnabledAutoRefresh}
                  checked={enabledAutoRefresh}
                  checkedChildren="Reconnection"
                  unCheckedChildren="Reconnection"
                  style={{ marginLeft: "10px" }}
                />
                <Switch
                  onChange={setEnabledSendPing}
                  checked={enabledSendPing}
                  checkedChildren="Ping"
                  unCheckedChildren="Ping"
                  style={{ marginLeft: "10px" }}
                />
              </div>
            }
          >
            {/* Summary Info */}
            <p style={{ marginBottom: "-1px" }}>
              <b
                className={
                  socket.current?.readyState === OPEN
                    ? "generalStyles__info"
                    : "generalStyles__warning"
                }
              >
                Status:
              </b>{" "}
              <b style={{ color: "white" }}>
                {socket.current?.readyState === OPEN && (
                  <Tag color="green">OPEN</Tag>
                )}
                {socket.current?.readyState === CONNECTING && (
                  <Tag color="cyan">CONNECTING</Tag>
                )}
                {socket.current?.readyState === CLOSING && (
                  <Tag color="orange">CLOSING</Tag>
                )}
                {socket.current?.readyState === CLOSED && (
                  <Tag color="red">CLOSED</Tag>
                )}
              </b>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Socket Connection:</b> <span>EDGE (ONLINE)</span>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Server:</b> <span>{serverId}</span>
            </p>
            <p style={{ marginBottom: "-1px" }}>
              <b>Channel Type:</b> <span>{channelType}</span>
            </p>
          </Card>
        </div>
      )}
    </Spin>
  );
}

export default MonitoringWebSocketConnection;
